import {
  FACILITY_AND_SPACE_FEATURES,
  FacilityAndSpaceFeatureDef,
  LocationScope,
  LocationVarKey,
  MergedVarsWithKeys,
  allFeatureVarKeys,
  findURLDef,
  isFeatureOn
} from "@swivl-mono/types";
import { Button, ButtonColor, ButtonSize } from "@swivl-mono/ui";
import { useEffect, useState } from "react";
import { BiEditAlt } from "react-icons/bi";

import { useMergedLocationVariables } from "../../../../../Hooks/useLocationVariables";
import { usePropertiesGetLocationIds } from "../../../../../Hooks/usePropertiesGetLocationIds";
import { CortexFeatureRow } from "../../Components/CortexFeatureRow";
import { TopicProvider, useTopicContext } from "../../Hooks/useTopicContext";

// Up to three plain vars per feature — on/off (switches), display name (all), wording (switches).
// Paired features also fetch their URL var, for the hint.
export const TOPIC_VARS: LocationVarKey[] = [
  ...allFeatureVarKeys(),
  ...FACILITY_AND_SPACE_FEATURES.flatMap((f) => (f.pairedUrlKey ? [findURLDef(f.pairedUrlKey)!.urlVarKey] : []))
];

interface FeatureGroup {
  id: string;
  title: string;
  caption: string;
  features: FacilityAndSpaceFeatureDef[];
}

/** One card per category, mirroring the Facility Info page. */
const FEATURE_GROUPS: FeatureGroup[] = [
  {
    id: "amenities",
    title: "Amenities",
    caption: "What your facility offers.",
    features: FACILITY_AND_SPACE_FEATURES.filter((f) => f.category === "amenity")
  },
  {
    id: "services",
    title: "Services",
    caption: "What your facility provides.",
    features: FACILITY_AND_SPACE_FEATURES.filter((f) => f.category === "service")
  },
  {
    id: "storage-types",
    title: "Storage Types",
    caption: "The unit types your facility offers.",
    features: FACILITY_AND_SPACE_FEATURES.filter((f) => f.category === "storage_type")
  },
  // Unit tags are written by FMS sync per unit — there is nothing to switch on or
  // off here, only the name each tag shows under (ENG-3209).
  {
    id: "unit-features",
    title: "Unit Feature Names",
    caption: "Tags your FMS puts on individual units. Rename them here; which units carry them comes from the FMS.",
    features: FACILITY_AND_SPACE_FEATURES.filter((f) => f.category === "unit_attribute")
  }
];

export function AmenitiesAndServicesTopic(): React.ReactElement {
  const ctx = useTopicContext();
  const page = usePropertiesGetLocationIds();
  // What a feature falls back to once its override here is deleted: whatever the
  // next scope up resolves to. At org scope there's nothing above, so it's off.
  const isOrgScope = page.type === LocationScope.ORG;
  const parentVars = useMergedLocationVariables(TOPIC_VARS, page.locationId != null ? page.groupId : undefined, undefined);
  // Which card is open. The worksheet owns the page-level editing flag; this
  // narrows it to a single card.
  const [openGroupId, setOpenGroupId] = useState<string | undefined>(undefined);

  // Save / Cancel clears the worksheet's flag — close the card to match.
  useEffect(() => {
    if (!ctx.isEditing && openGroupId) setOpenGroupId(undefined);
  }, [ctx.isEditing, openGroupId]);

  const handleEdit = (groupId: string): void => {
    setOpenGroupId(groupId);
    ctx.setEditing(true);
  };

  return (
    <div className="flex flex-col gap-4">
      {FEATURE_GROUPS.map((group) => (
        <FeatureCard
          key={group.id}
          group={group}
          isEditing={ctx.isEditing && openGroupId === group.id}
          isOrgScope={isOrgScope}
          parentVars={parentVars}
          onEdit={() => handleEdit(group.id)}
        />
      ))}
    </div>
  );
}

// ─── Card ──────────────────────────────────────────────────────────────────

/**
 * What a revert restores, in words. For a switch that's the parent's on/off; for
 * a unit tag (no switch) it's the name the parent scope shows the tag under.
 */
function revertsToFor(feature: FacilityAndSpaceFeatureDef, isOrgScope: boolean, parentVars: MergedVarsWithKeys<LocationVarKey> | undefined): string {
  const parent = (key: LocationVarKey | undefined): string | undefined => (isOrgScope || !key ? undefined : parentVars?.[key]?.value);
  if (!feature.cortexVarKey) return (parent(feature.nameVarKey) ?? "").trim() || feature.label;
  const parentOn = parent(feature.cortexVarKey);
  if (parentOn === undefined) return "No";
  return isFeatureOn(parentOn) ? "Yes" : "No";
}

interface FeatureCardProps {
  group: FeatureGroup;
  isEditing: boolean;
  isOrgScope: boolean;
  parentVars: MergedVarsWithKeys<LocationVarKey> | undefined;
  onEdit: () => void;
}

function FeatureCard({ group, isEditing, isOrgScope, parentVars, onEdit }: FeatureCardProps): React.ReactElement {
  const ctx = useTopicContext();

  return (
    <section className="overflow-hidden rounded-lg border border-grey-200 bg-white">
      <div className="flex items-center justify-between gap-4 px-4 py-3">
        <div className="min-w-0">
          <h3 className="heading-base-alt text-grey-900">{group.title}</h3>
          <p className="body-sm mt-0.5 text-grey-600">{group.caption}</p>
        </div>
        {isEditing ? (
          <div className="flex shrink-0 items-center gap-2">
            <Button color={ButtonColor.WHITE} size={ButtonSize.SMALL} disabled={ctx.saving} onClick={ctx.cancelEdits}>
              Cancel
            </Button>
            <Button color={ButtonColor.ORANGE} size={ButtonSize.SMALL} disabled={ctx.saving} onClickAsync={ctx.saveTopic}>
              {ctx.saving ? "Saving…" : "Save"}
            </Button>
          </div>
        ) : (
          <button
            type="button"
            onClick={onEdit}
            className="body-sm-alt flex shrink-0 cursor-pointer items-center gap-2 text-grey-900 hover:text-orange-500"
          >
            Edit
            <BiEditAlt className="size-4" />
          </button>
        )}
      </div>

      {/* Only this card's rows see edit mode; the rest of the topic stays read-only. */}
      <TopicProvider {...ctx} isEditing={isEditing}>
        <div className="border-t border-grey-200">
          {group.features.map((feature) => (
            <CortexFeatureRow
              key={feature.key}
              feature={feature}
              isOrgScope={isOrgScope}
              parentInheritance={parentVars?.[feature.cortexVarKey ?? feature.nameVarKey]?.inheritance}
              revertsTo={revertsToFor(feature, isOrgScope, parentVars)}
            />
          ))}
        </div>
      </TopicProvider>
    </section>
  );
}
